// Multi-cluster portfolio strip — shown on Overview when the account has >1
// cluster. Portfolio totals + a compact per-cluster row (cost, idle%, spot cov).
// Reads /finops/v2/overview. Renders nothing for single-cluster accounts.

import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Layers } from 'lucide-react';
import { useOverview } from '../../hooks/useFinopsV2';
import { formatMoney, formatPct } from './finopsFormat';
import { SectionHeader } from './finopsUi';

export default function FinopsOverviewStrip({ hours, activeClusterId }) {
  const { data, loading, available } = useOverview(hours);
  if (!available || loading) return null;
  if (!data || (data.cluster_count || 0) <= 1) return null;

  return (
    <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
      <CardContent className="p-5">
        <SectionHeader title="Portfolio" hint={`${data.cluster_count} clusters · ${hours}h`} />
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-100 dark:divide-slate-800 border-y border-slate-100 dark:border-slate-800 -mx-5 mb-4">
          <Metric label="Total capacity" value={formatMoney(data.total_capacity_cost_usd, { compact: true })} />
          <Metric label="Total idle" value={formatMoney(data.total_idle_cost_usd, { compact: true })} tint="text-amber-600 dark:text-amber-400" />
          <Metric label="Portfolio idle" value={formatPct(data.portfolio_idle_pct)} />
          <Metric label="Spot spend" value={formatMoney(data.total_spot_cost_usd, { compact: true })} tint="text-violet-600 dark:text-violet-400" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-surface-border">
                {['Cluster', 'Cost', 'Idle', 'Idle %', 'Spot cov', 'Nodes'].map((h, i) => (
                  <th key={h} className={`py-1.5 px-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.clusters || []).map((c) => (
                <tr key={c.cluster_id}
                    className={`border-b border-slate-100 dark:border-slate-800 ${c.cluster_id === activeClusterId ? 'bg-blue-50/50 dark:bg-blue-500/5' : ''}`}>
                  <td className="py-1.5 px-2 font-medium text-slate-900 dark:text-white flex items-center gap-1.5">
                    {c.cluster_id === activeClusterId && <Layers className="w-3.5 h-3.5 text-blue-500" />}
                    <span className="truncate max-w-[220px]">{c.name}</span>
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-slate-900 dark:text-white">{formatMoney(c.capacity_cost_usd, { compact: true })}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-amber-600 dark:text-amber-400">{formatMoney(c.idle_cost_usd, { compact: true })}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-slate-600 dark:text-text-secondary">{formatPct(c.idle_pct)}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-slate-600 dark:text-text-secondary">{formatPct(c.spot_coverage_pct)}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-slate-600 dark:text-text-secondary">{c.node_count ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function Metric({ label, value, tint }) {
  return (
    <div className="px-5 py-3 min-w-0">
      <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1 truncate">{label}</p>
      <p className={`text-xl font-semibold tabular-nums tracking-tight ${tint || 'text-slate-900 dark:text-white'}`}>{value}</p>
    </div>
  );
}
